// Batch E3.6 — Kanban board hook.
//
// Same shape as usePipelineCounts(): module-level cache + 30 s stale +
// subscriber fan-out. Exports:
//
//   useKanban()                       — fetch + cache subscription
//   invalidateKanban()                — manual refetch (used after PATCH)
//   moveCardOptimistically(id, from, to)
//                                     — mutates the cache in place so the
//                                       drag is immediately reflected even
//                                       before the PATCH round-trips
//   findCardById(id)                  — convenience for the dnd handlers
//
// Production hardening (Phase 36 — Dashboard P0 follow-up #3, mirrors
// usePipelineCounts.js):
//   • 10 s request timeout — silent fetch failures used to leave the
//     consumer stuck on a loading skeleton forever.
//   • Explicit error state ("timeout" / "unauthorized" / "network")
//     surfaced so Pipeline.jsx can render a graceful empty / retry
//     state instead of pulsing rectangles.
//   • One-time automatic retry on 401 with a 1.5 s delay — covers the
//     well-documented auth race during first page load where the
//     session cookie hasn't yet been set when the hook fires.

import { useCallback, useEffect, useState } from "react";
import api from "./api";

const STALE_MS = 30_000;
const REQUEST_TIMEOUT_MS = 10_000;
const RETRY_DELAY_MS = 1_500;

let CACHE = null;          // { columns, total, truncated_per_column, fetchedAt }
let INFLIGHT = null;
let LAST_ERROR = null;     // "timeout" | "unauthorized" | "network" | null
const SUBSCRIBERS = new Set();

function notify() {
  SUBSCRIBERS.forEach((cb) => { try { cb(); } catch { /* noop */ } });
}

async function fetchBoard({ _isRetry = false } = {}) {
  if (INFLIGHT) return INFLIGHT;
  INFLIGHT = api
    .get(`/pipeline/board`, {
      timeout: REQUEST_TIMEOUT_MS,
    })
    .then((r) => {
      CACHE = { ...r.data, fetchedAt: Date.now() };
      LAST_ERROR = null;
      INFLIGHT = null;
      notify();
      return CACHE;
    })
    .catch(async (e) => {
      INFLIGHT = null;
      // Auto-retry once on 401 (auth race during first page load).
      const status = e?.response?.status;
      if (!_isRetry && status === 401) {
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
        return fetchBoard({ _isRetry: true });
      }
      LAST_ERROR =
        e?.code === "ECONNABORTED"
          ? "timeout"
          : status === 401
            ? "unauthorized"
            : "network";
      notify();
      throw e;
    });
  return INFLIGHT;
}

export function invalidateKanban() {
  CACHE = null;
  INFLIGHT = null;
  LAST_ERROR = null;
  notify();
}

export function moveCardOptimistically(cardId, fromStage, toStage) {
  if (!CACHE || !CACHE.columns) return null;
  if (fromStage === toStage) return null;
  const from = CACHE.columns[fromStage];
  const to = CACHE.columns[toStage];
  if (!from || !to) return null;
  const idx = from.deals.findIndex((d) => d.id === cardId);
  if (idx < 0) return null;
  const [card] = from.deals.splice(idx, 1);
  card.pipeline_stage = toStage;
  to.deals.unshift(card);
  from.count = Math.max(0, (from.count || 0) - 1);
  to.count = (to.count || 0) + 1;
  notify();
  return { card, from: fromStage, to: toStage };
}

// Roll a move back if the PATCH fails.
export function revertCardMove(cardId, fromStage, toStage) {
  if (!CACHE || !CACHE.columns) return;
  const from = CACHE.columns[fromStage]; // original
  const to = CACHE.columns[toStage];     // current location after optimistic
  if (!from || !to) return;
  const idx = to.deals.findIndex((d) => d.id === cardId);
  if (idx < 0) return;
  const [card] = to.deals.splice(idx, 1);
  card.pipeline_stage = fromStage;
  from.deals.unshift(card);
  to.count = Math.max(0, (to.count || 0) - 1);
  from.count = (from.count || 0) + 1;
  notify();
}

export function findCardById(cardId) {
  if (!CACHE || !CACHE.columns) return null;
  for (const col of Object.values(CACHE.columns)) {
    const c = col.deals.find((d) => d.id === cardId);
    if (c) return c;
  }
  return null;
}

export default function useKanban() {
  const [, tick] = useState(0);

  useEffect(() => {
    const cb = () => tick((n) => n + 1);
    SUBSCRIBERS.add(cb);
    return () => { SUBSCRIBERS.delete(cb); };
  }, []);

  useEffect(() => {
    const isFresh = CACHE && Date.now() - CACHE.fetchedAt < STALE_MS;
    if (!isFresh) {
      fetchBoard().catch(() => { /* surfaced via LAST_ERROR + notify */ });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => {
    invalidateKanban();
    return fetchBoard().catch(() => null);
  }, []);

  return {
    columns: CACHE?.columns || null,
    total:   CACHE?.total   ?? null,
    truncatedPerColumn: CACHE?.truncated_per_column ?? 50,
    // Loading is true only while we have NEITHER cache NOR a final error.
    // Once an error lands, loading drops to false so consumers can render
    // their error / empty fallback (preventing infinite skeleton state).
    loading: !CACHE && !LAST_ERROR,
    error: LAST_ERROR,
    refetch,
  };
}
